import {
  ArrowDownIcon,
  ArrowRightIcon,
  ClockIcon,
  HandshakeIcon,
  MinimizeIcon,
  SendIcon,
  ShuffleIcon,
  ZapIcon,
} from 'lucide-react';

import React, { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type {
  DebtRecommendationDto,
  GroupWithDashboard,
  PendingSettlementDto,
} from '@splitfiesta/shared';
import { Avatar, Badge, Button, Card } from '@splitfiesta/ui';

import { CreateSettlementModal } from '@/components/groups/CreateSettlementModal';
import { RecordPaymentModal } from '@/components/groups/RecordPaymentModal';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';

import { useTypedApi } from '@/hooks/useTypedApi';

import { resolveUserName } from '@/lib/displayName';

import { useAuthStore } from '@/stores/authStore';

interface PaymentSuggestionsProps {
  group: GroupWithDashboard;
}

interface BilateralDebt {
  fromUser: {
    id: string;
    name: string;
    profileImage?: string;
  };
  toUser: {
    id: string;
    name: string;
    profileImage?: string;
  };
  amount: number;
  currency: string;
}

export function PaymentSuggestions({ group }: PaymentSuggestionsProps) {
  const { t } = useTranslation();
  const api = useTypedApi();
  const user = useAuthStore(state => state.user);

  // Check if current user is the group owner
  const isGroupOwner = group.userRole === 'owner';

  // Modal state for recording payments (creditor-initiated or owner-initiated)
  const [isRecordPaymentModalOpen, setIsRecordPaymentModalOpen] =
    useState(false);
  const [preselectedDebtorId, setPreselectedDebtorId] = useState<
    string | undefined
  >();
  // For owner-initiated settlements - track the creditor
  const [ownerModeCreditorId, setOwnerModeCreditorId] = useState<
    string | undefined
  >();

  // Modal state for settling up (debtor-initiated)
  const [isSettleUpModalOpen, setIsSettleUpModalOpen] = useState(false);
  const [preselectedCreditorId, setPreselectedCreditorId] = useState<
    string | undefined
  >();

  // Modal state for flexible settlement (owner can record any payment between any two members)
  const [isFlexibleSettlementModalOpen, setIsFlexibleSettlementModalOpen] =
    useState(false);

  // Handle clicking "Record Payment" on a specific debt suggestion
  // For owner mode, also track the creditor when clicking on a debt between other members
  const handleRecordPayment = (
    e: React.MouseEvent<HTMLButtonElement>,
    debtorId: string,
    creditorId?: string
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setPreselectedDebtorId(debtorId);
    // If creditorId is provided and user is not the creditor, this is owner mode
    setOwnerModeCreditorId(creditorId !== user?.id ? creditorId : undefined);
    setIsRecordPaymentModalOpen(true);
  };

  // Handle clicking "Settle Up" on a specific debt suggestion
  const handleSettleUp = (
    e: React.MouseEvent<HTMLButtonElement>,
    creditorId: string
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setPreselectedCreditorId(creditorId);
    setIsSettleUpModalOpen(true);
  };

  // Load settlements namespace for pending settlements translations
  useNamespace('settlements');

  // Fetch simplified debts from API if enabled
  // Don't filter by user - we want ALL recommendations for display
  const {
    data: simplifiedDebtsData,
    isLoading: isLoadingSimplified,
    error: simplifiedError,
  } = useQuery({
    queryKey: queryKeys.simplifiedDebts(group.id, false),
    queryFn: () => api.groups.getSimplifiedDebts(group.id, false), // Don't filter - show all optimizations
    enabled: !!group.simplifyDebts,
  });

  // Calculate bilateral debts from balances for non-simplified groups
  const bilateralDebts = useMemo<BilateralDebt[]>(() => {
    if (group.simplifyDebts) {
      return [];
    }

    const debts: BilateralDebt[] = [];
    const { balances } = group;

    // Create copies of balances to track remaining amounts
    const creditors = balances
      .filter(b => b.balance > 0)
      .map(b => ({ ...b, remaining: b.balance }))
      .sort((a, b) => b.remaining - a.remaining); // Sort by amount owed (largest first)

    const debtors = balances
      .filter(b => b.balance < 0)
      .map(b => ({ ...b, remaining: Math.abs(b.balance) }))
      .sort((a, b) => b.remaining - a.remaining); // Sort by amount owing (largest first)

    // Match debtors with creditors using a greedy approach
    // This creates a more intuitive set of payments without full simplification
    for (const debtor of debtors) {
      for (const creditor of creditors) {
        if (debtor.remaining <= 0.01 || creditor.remaining <= 0.01) {
          continue;
        }

        const amountToSettle = Math.min(debtor.remaining, creditor.remaining);

        if (amountToSettle > 0.01) {
          debts.push({
            fromUser: {
              id: debtor.userId,
              name: debtor.userName,
              profileImage: debtor.profileImage,
            },
            toUser: {
              id: creditor.userId,
              name: creditor.userName,
              profileImage: creditor.profileImage,
            },
            amount: amountToSettle,
            currency: group.currency,
          });

          // Update remaining amounts
          debtor.remaining -= amountToSettle;
          creditor.remaining -= amountToSettle;
        }
      }
    }

    return debts;
  }, [group]);

  // Get the appropriate debts based on simplification setting
  const simplifiedDebtsResponse = simplifiedDebtsData?.data;
  const simplifiedDebts: DebtRecommendationDto[] =
    simplifiedDebtsResponse && 'recommendations' in simplifiedDebtsResponse
      ? simplifiedDebtsResponse.recommendations
      : [];

  // Extract pending settlements from simplified debts response
  const pendingSettlements = useMemo<PendingSettlementDto[]>(
    () =>
      simplifiedDebtsResponse && 'pendingSettlements' in simplifiedDebtsResponse
        ? simplifiedDebtsResponse.pendingSettlements
        : [],
    [simplifiedDebtsResponse]
  );

  const debtsUnsorted = group.simplifyDebts ? simplifiedDebts : bilateralDebts;
  const isLoading = group.simplifyDebts ? isLoadingSimplified : false;
  const error = group.simplifyDebts ? simplifiedError : null;

  // Sort debts so the ones involving the current user appear first
  const debtsToShow = useMemo(() => {
    const userId = user?.id;
    if (!userId) return debtsUnsorted;

    return [...debtsUnsorted].sort((a, b) => {
      const aInvolvesUser = a.fromUser.id === userId || a.toUser.id === userId;
      const bInvolvesUser = b.fromUser.id === userId || b.toUser.id === userId;

      // User-involved debts come first
      if (aInvolvesUser && !bInvolvesUser) return -1;
      if (!aInvolvesUser && bInvolvesUser) return 1;
      return 0;
    });
  }, [debtsUnsorted, user]);

  // Split pending settlements: user-involved ones at top, others at bottom
  const { userPendingSettlements, otherPendingSettlements } = useMemo(() => {
    const userId = user?.id;
    if (!userId) {
      return {
        userPendingSettlements: [],
        otherPendingSettlements: pendingSettlements,
      };
    }

    const userSettlements: PendingSettlementDto[] = [];
    const otherSettlements: PendingSettlementDto[] = [];

    for (const settlement of pendingSettlements) {
      if (
        settlement.fromUser.id === userId ||
        settlement.toUser.id === userId
      ) {
        userSettlements.push(settlement);
      } else {
        otherSettlements.push(settlement);
      }
    }

    return {
      userPendingSettlements: userSettlements,
      otherPendingSettlements: otherSettlements,
    };
  }, [pendingSettlements, user]);

  // Check if there are actual non-zero balances (regardless of pending settlements)
  const hasNonZeroBalances = useMemo(() => {
    return group.balances?.some(b => Math.abs(b.balance) > 0.01) ?? false;
  }, [group.balances]);

  // Determine if we're in "pending settlements covering debts" state
  const hasPendingSettlementsCoveringDebts =
    debtsToShow.length === 0 &&
    hasNonZeroBalances &&
    pendingSettlements.length > 0;

  // Create modal elements to include in all returns
  const recordPaymentModalElement = (
    <RecordPaymentModal
      isOpen={isRecordPaymentModalOpen}
      onClose={() => {
        setIsRecordPaymentModalOpen(false);
        setPreselectedDebtorId(undefined);
        setOwnerModeCreditorId(undefined);
      }}
      group={group}
      preselectedDebtorId={preselectedDebtorId}
      ownerMode={!!ownerModeCreditorId}
      preselectedCreditorId={ownerModeCreditorId}
    />
  );

  const settleUpModalElement = (
    <CreateSettlementModal
      isOpen={isSettleUpModalOpen}
      onClose={() => {
        setIsSettleUpModalOpen(false);
        setPreselectedCreditorId(undefined);
      }}
      group={group}
      preselectedCreditorId={preselectedCreditorId}
    />
  );

  const flexibleSettlementModalElement = (
    <RecordPaymentModal
      isOpen={isFlexibleSettlementModalOpen}
      onClose={() => setIsFlexibleSettlementModalOpen(false)}
      group={group}
      flexibleOwnerMode={true}
    />
  );

  // Show loading state
  if (isLoading) {
    return (
      <>
        <Card data-testid="payment-suggestions-loading">
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t('common:loading.text')}...
            </div>
          </div>
        </Card>
        {recordPaymentModalElement}
        {settleUpModalElement}
        {flexibleSettlementModalElement}
      </>
    );
  }

  // Show error state
  if (error) {
    return (
      <>
        <Card data-testid="payment-suggestions-error">
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-red-500 dark:text-red-400">
              {t('common:error.generic')}
            </div>
          </div>
        </Card>
        {recordPaymentModalElement}
        {settleUpModalElement}
        {flexibleSettlementModalElement}
      </>
    );
  }

  // Show pending settlements state when debts are covered by pending settlements
  if (hasPendingSettlementsCoveringDebts) {
    return (
      <>
        <Card
          data-testid="payment-suggestions-pending"
          className="p-6"
          padding="none"
        >
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <ClockIcon className="w-5 h-5 text-amber-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('settlements:labels.pendingSettlements')}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('settlements:labels.pendingSettlementsDescription')}
            </p>
            <div className="space-y-3">
              {pendingSettlements.map((settlement, index) => (
                <div
                  key={settlement.id || index}
                  className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800"
                  data-testid={`pending-settlement-${index}`}
                >
                  {/* Mobile: stacked layout */}
                  <div className="flex flex-col items-center sm:hidden">
                    <div className="flex items-center gap-2">
                      <Avatar
                        name={settlement.fromUser.name}
                        src={settlement.fromUser.profileImage}
                        size="sm"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {resolveUserName(settlement.fromUser.name, t)}
                      </span>
                    </div>
                    <ArrowDownIcon className="w-3 h-3 text-amber-500 -mt-1.5" />
                    <div className="text-base font-bold text-amber-600 dark:text-amber-400">
                      <CurrencyDisplay
                        amount={settlement.amount}
                        currency={settlement.currency}
                      />
                    </div>
                    <ArrowDownIcon className="w-3 h-3 text-amber-500 -mb-1.5" />
                    <div className="flex items-center gap-2">
                      <Avatar
                        name={settlement.toUser.name}
                        src={settlement.toUser.profileImage}
                        size="sm"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {resolveUserName(settlement.toUser.name, t)}
                      </span>
                    </div>
                  </div>

                  {/* Desktop: horizontal layout */}
                  <div className="hidden sm:flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Avatar
                        name={settlement.fromUser.name}
                        src={settlement.fromUser.profileImage}
                        size="sm"
                        className="shrink-0"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {resolveUserName(settlement.fromUser.name, t)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 px-3">
                      <ArrowRightIcon className="w-4 h-4 text-amber-500 shrink-0" />
                      <div className="text-sm font-bold text-amber-600 dark:text-amber-400">
                        <CurrencyDisplay
                          amount={settlement.amount}
                          currency={settlement.currency}
                        />
                      </div>
                      <ArrowRightIcon className="w-4 h-4 text-amber-500 shrink-0" />
                    </div>
                    <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate text-right">
                        {resolveUserName(settlement.toUser.name, t)}
                      </span>
                      <Avatar
                        name={settlement.toUser.name}
                        src={settlement.toUser.profileImage}
                        size="sm"
                        className="shrink-0"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
        {recordPaymentModalElement}
        {settleUpModalElement}
        {flexibleSettlementModalElement}
      </>
    );
  }

  // Show "all settled" only when there are truly no debts (balances are zero)
  if (debtsToShow.length === 0) {
    return (
      <>
        <Card data-testid="payment-suggestions-none" className="p-6">
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('groups:smartPayments.noDebts')}
            </p>
          </div>
        </Card>
        {recordPaymentModalElement}
        {settleUpModalElement}
        {flexibleSettlementModalElement}
      </>
    );
  }

  return (
    <>
      <Card data-testid="payment-suggestions" className="p-6" padding="none">
        <div className="space-y-4">
          {/* Header with stats */}
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <ZapIcon className="w-5 h-5 text-orange-500" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {group.simplifyDebts
                      ? t('groups:smartPayments.title')
                      : t('groups:paymentSuggestions.title')}
                  </h3>
                  {group.simplifyDebts && (
                    <Badge variant="success" size="sm">
                      {t('groups:smartPayments.optimized')}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {group.simplifyDebts
                    ? t('groups:smartPayments.description')
                    : t('groups:paymentSuggestions.description')}
                </p>
              </div>
              {/* Record Any Payment button - visible only to owner */}
              {isGroupOwner && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsFlexibleSettlementModalOpen(true)}
                  className="shrink-0 w-full sm:w-auto"
                  data-testid="flexible-settlement-btn"
                >
                  <ShuffleIcon className="h-4 w-4 mr-2" />
                  {t('settlements:actions.recordAnyPayment')}
                </Button>
              )}
            </div>
          </div>

          {/* User's pending settlements at the top */}
          {userPendingSettlements.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ClockIcon className="w-4 h-4 text-amber-500" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t('settlements:labels.pendingSettlements')}
                </h4>
              </div>
              {userPendingSettlements.map((settlement, index) => (
                <div
                  key={settlement.id || `user-pending-${index}`}
                  className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800"
                  data-testid={`user-pending-settlement-${index}`}
                >
                  {/* Mobile: stacked layout */}
                  <div className="flex flex-col items-center sm:hidden">
                    <div className="flex items-center gap-2">
                      <Avatar
                        name={settlement.fromUser.name}
                        src={settlement.fromUser.profileImage}
                        size="sm"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {resolveUserName(settlement.fromUser.name, t)}
                      </span>
                    </div>
                    <ArrowDownIcon className="w-3 h-3 text-amber-500 -mt-1.5" />
                    <div className="text-base font-bold text-amber-600 dark:text-amber-400">
                      <CurrencyDisplay
                        amount={settlement.amount}
                        currency={settlement.currency}
                      />
                    </div>
                    <ArrowDownIcon className="w-3 h-3 text-amber-500 -mb-1.5" />
                    <div className="flex items-center gap-2">
                      <Avatar
                        name={settlement.toUser.name}
                        src={settlement.toUser.profileImage}
                        size="sm"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {resolveUserName(settlement.toUser.name, t)}
                      </span>
                    </div>
                  </div>

                  {/* Desktop: horizontal layout */}
                  <div className="hidden sm:flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Avatar
                        name={settlement.fromUser.name}
                        src={settlement.fromUser.profileImage}
                        size="sm"
                        className="shrink-0"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {resolveUserName(settlement.fromUser.name, t)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 px-3">
                      <ArrowRightIcon className="w-4 h-4 text-amber-500 shrink-0" />
                      <div className="text-sm font-bold text-amber-600 dark:text-amber-400">
                        <CurrencyDisplay
                          amount={settlement.amount}
                          currency={settlement.currency}
                        />
                      </div>
                      <ArrowRightIcon className="w-4 h-4 text-amber-500 shrink-0" />
                    </div>
                    <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate text-right">
                        {resolveUserName(settlement.toUser.name, t)}
                      </span>
                      <Avatar
                        name={settlement.toUser.name}
                        src={settlement.toUser.profileImage}
                        size="sm"
                        className="shrink-0"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Payment suggestions */}
          <div
            className={`space-y-3 ${userPendingSettlements.length > 0 ? 'pt-4 border-t border-gray-200 dark:border-gray-700' : ''}`}
          >
            {debtsToShow.map((debt, index) => {
              const isCreditor = debt.toUser.id === user?.id;
              const isDebtor = debt.fromUser.id === user?.id;
              return (
                <div
                  key={`${debt.fromUser.id}-${debt.toUser.id}-${debt.currency}-${index}`}
                  className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-100 dark:border-orange-800"
                  data-testid={`payment-suggestion-${index}`}
                >
                  {/* Mobile: stacked layout */}
                  <div className="flex flex-col items-center sm:hidden">
                    <div className="flex items-center gap-2">
                      <Avatar
                        name={debt.fromUser.name}
                        src={debt.fromUser.profileImage}
                        size="sm"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {resolveUserName(debt.fromUser.name, t)}
                      </span>
                    </div>
                    <ArrowDownIcon className="w-3 h-3 text-orange-500 -mt-1.5" />
                    <div className="text-base font-bold text-orange-600 dark:text-orange-400">
                      <CurrencyDisplay
                        amount={debt.amount}
                        currency={debt.currency}
                      />
                    </div>
                    <ArrowDownIcon className="w-3 h-3 text-orange-500 -mb-1.5" />
                    <div className="flex items-center gap-2">
                      <Avatar
                        name={debt.toUser.name}
                        src={debt.toUser.profileImage}
                        size="sm"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {resolveUserName(debt.toUser.name, t)}
                      </span>
                    </div>
                    {/* Record Payment button for mobile (creditor or owner can record) */}
                    {(isCreditor || isGroupOwner) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={e =>
                          handleRecordPayment(
                            e,
                            debt.fromUser.id,
                            debt.toUser.id
                          )
                        }
                        className="mt-3 w-full"
                        data-testid={`record-payment-btn-${index}`}
                      >
                        <HandshakeIcon className="h-4 w-4 mr-2 shrink-0" />
                        <span className="truncate max-w-[150px]">
                          {resolveUserName(debt.fromUser.name, t)}
                        </span>
                        <span className="shrink-0 ml-1">
                          {isCreditor
                            ? t('settlements:actions.recordPaymentFromSuffix')
                            : t(
                                'settlements:actions.recordPaymentAsOwnerSuffix'
                              )}
                        </span>
                      </Button>
                    )}
                    {/* Settle Up button for mobile (only if current user is debtor) */}
                    {isDebtor && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={e => handleSettleUp(e, debt.toUser.id)}
                        className="mt-3 w-full"
                        data-testid={`settle-up-btn-${index}`}
                      >
                        <SendIcon className="h-4 w-4 mr-2" />
                        {t('settlements:actions.settle')}
                      </Button>
                    )}
                  </div>

                  {/* Desktop: horizontal layout */}
                  <div className="hidden sm:block">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Avatar
                          name={debt.fromUser.name}
                          src={debt.fromUser.profileImage}
                          size="sm"
                          className="shrink-0"
                        />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {resolveUserName(debt.fromUser.name, t)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-3 shrink-0">
                        <ArrowRightIcon className="w-4 h-4 text-orange-500 shrink-0" />
                        <div className="text-sm font-bold text-orange-600 dark:text-orange-400">
                          <CurrencyDisplay
                            amount={debt.amount}
                            currency={debt.currency}
                          />
                        </div>
                        <ArrowRightIcon className="w-4 h-4 text-orange-500 shrink-0" />
                      </div>
                      <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate text-right">
                          {resolveUserName(debt.toUser.name, t)}
                        </span>
                        <Avatar
                          name={debt.toUser.name}
                          src={debt.toUser.profileImage}
                          size="sm"
                          className="shrink-0"
                        />
                      </div>
                    </div>
                    {/* Action buttons row (creditor/owner can record, debtor can settle up) */}
                    {(isCreditor || isGroupOwner || isDebtor) && (
                      <div className="flex flex-row justify-end items-center gap-2 mt-3">
                        {(isCreditor || isGroupOwner) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="shrink-0"
                            onClick={e =>
                              handleRecordPayment(
                                e,
                                debt.fromUser.id,
                                debt.toUser.id
                              )
                            }
                            data-testid={`record-payment-btn-desktop-${index}`}
                          >
                            <HandshakeIcon className="h-4 w-4 mr-2 shrink-0" />
                            <span className="whitespace-nowrap">
                              {resolveUserName(debt.fromUser.name, t)}
                            </span>
                            <span className="shrink-0 ml-1">
                              {isCreditor
                                ? t(
                                    'settlements:actions.recordPaymentFromSuffix'
                                  )
                                : t(
                                    'settlements:actions.recordPaymentAsOwnerSuffix'
                                  )}
                            </span>
                          </Button>
                        )}
                        {isDebtor && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="shrink-0"
                            onClick={e => handleSettleUp(e, debt.toUser.id)}
                            data-testid={`settle-up-btn-desktop-${index}`}
                          >
                            <SendIcon className="h-4 w-4 mr-2" />
                            {t('settlements:actions.settle')}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Other pending settlements section (not involving current user) */}
          {otherPendingSettlements.length > 0 && (
            <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <ClockIcon className="w-4 h-4 text-amber-500" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t('settlements:labels.pendingSettlements')}
                </h4>
              </div>
              {otherPendingSettlements.map((settlement, index) => (
                <div
                  key={settlement.id || `other-pending-${index}`}
                  className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800"
                  data-testid={`pending-settlement-mixed-${index}`}
                >
                  {/* Mobile: stacked layout */}
                  <div className="flex flex-col items-center sm:hidden">
                    <div className="flex items-center gap-2">
                      <Avatar
                        name={settlement.fromUser.name}
                        src={settlement.fromUser.profileImage}
                        size="sm"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {resolveUserName(settlement.fromUser.name, t)}
                      </span>
                    </div>
                    <ArrowDownIcon className="w-3 h-3 text-amber-500 -mt-1.5" />
                    <div className="text-base font-bold text-amber-600 dark:text-amber-400">
                      <CurrencyDisplay
                        amount={settlement.amount}
                        currency={settlement.currency}
                      />
                    </div>
                    <ArrowDownIcon className="w-3 h-3 text-amber-500 -mb-1.5" />
                    <div className="flex items-center gap-2">
                      <Avatar
                        name={settlement.toUser.name}
                        src={settlement.toUser.profileImage}
                        size="sm"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {resolveUserName(settlement.toUser.name, t)}
                      </span>
                    </div>
                  </div>

                  {/* Desktop: horizontal layout */}
                  <div className="hidden sm:flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Avatar
                        name={settlement.fromUser.name}
                        src={settlement.fromUser.profileImage}
                        size="sm"
                        className="shrink-0"
                      />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {resolveUserName(settlement.fromUser.name, t)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 px-3">
                      <ArrowRightIcon className="w-4 h-4 text-amber-500 shrink-0" />
                      <div className="text-sm font-bold text-amber-600 dark:text-amber-400">
                        <CurrencyDisplay
                          amount={settlement.amount}
                          currency={settlement.currency}
                        />
                      </div>
                      <ArrowRightIcon className="w-4 h-4 text-amber-500 shrink-0" />
                    </div>
                    <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate text-right">
                        {resolveUserName(settlement.toUser.name, t)}
                      </span>
                      <Avatar
                        name={settlement.toUser.name}
                        src={settlement.toUser.profileImage}
                        size="sm"
                        className="shrink-0"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Help text */}
          <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <MinimizeIcon className="w-4 h-4 mt-0.5 text-orange-500 shrink-0" />
              <div>
                <p className="font-medium mb-1">
                  {group.simplifyDebts
                    ? t('groups:smartPayments.howItWorks.title')
                    : t('groups:paymentSuggestions.howItWorks.title')}
                </p>
                <p>
                  {group.simplifyDebts
                    ? t('groups:smartPayments.howItWorks.description')
                    : t('groups:paymentSuggestions.howItWorks.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Record Payment Modal (creditor-initiated settlement) */}
      {recordPaymentModalElement}

      {/* Settle Up Modal (debtor-initiated settlement) */}
      {settleUpModalElement}

      {/* Flexible Settlement Modal (owner can record any payment between members) */}
      {flexibleSettlementModalElement}
    </>
  );
}
